import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Building2, Mail, Lock, Save, ArrowLeft, KeyRound, RefreshCw, ShieldCheck, ExternalLink } from 'lucide-react';
import QRCode from 'react-qr-code';
import api from '../../lib/api';
import Button from '../../components/ui/Button';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import FileUploader from '../../components/ui/FileUploader';
import OpenKanbanButton from '../../components/OpenKanbanButton';
import { useAuthStore } from '../../stores/authStore';
import { resolveMediaUrl } from '../../lib/mediaUrl';

function authProviderLabel(provider: string): string {
  const map: Record<string, string> = {
    email: 'Email & password',
    google: 'Google',
    apple: 'Apple',
    github: 'GitHub',
    facebook: 'Facebook',
    twitter: 'Twitter',
    discord: 'Discord',
    slack: 'Slack',
    linkedin: 'LinkedIn',
    linkedin_oidc: 'LinkedIn',
    azure: 'Microsoft',
  };
  return map[provider] ?? provider;
}

type MfaStatus = {
  enabled: boolean;
  provider: 'supabase';
  factors: Array<{
    id: string;
    type: string;
    friendlyName: string | null;
    status: string;
  }>;
};

type MfaEnrollment = {
  factorId: string;
  qrCode: string | null;
  secret: string | null;
  uri: string | null;
};

export default function Profile() {
  const navigate = useNavigate();
  const { user: currentUser, fetchUser } = useAuthStore();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile form
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const [consent, setConsent] = useState({ marketingOptIn: false, dataProcessing: true });
  const [notifPrefs, setNotifPrefs] = useState({ inApp: true, email: false });
  const [mfaStatus, setMfaStatus] = useState<MfaStatus | null>(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaEnrollment, setMfaEnrollment] = useState<MfaEnrollment | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaMessage, setMfaMessage] = useState('');
  const [mfaBusy, setMfaBusy] = useState(false);

  const showKanbanAccess = currentUser?.role !== 'ADMIN';

  useEffect(() => {
    loadProfile();
    loadExtras();
    loadMfaStatus();
  }, []);

  const loadExtras = async () => {
    try {
      const [, dc, np] = await Promise.all([
        api.get('/users/me/settings').catch(() => ({ data: {} })),
        api.get('/users/me/data-consent').catch(() => ({ data: {} })),
        api.get('/notifications/preferences').catch(() => ({ data: {} })),
      ]);
      setConsent({
        marketingOptIn: !!dc.data?.marketingOptIn,
        dataProcessing: dc.data?.dataProcessing !== false,
      });
      setNotifPrefs({
        inApp: np.data?.inApp !== false,
        email: !!np.data?.email,
      });
    } catch {
      /* ignore */
    }
  };

  const saveConsent = async () => {
    try {
      await api.put('/users/me/data-consent', consent);
      alert('Consent preferences saved');
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed');
    }
  };

  const saveNotifPrefs = async () => {
    try {
      await api.put('/notifications/preferences', notifPrefs);
      alert('Notification preferences saved');
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed');
    }
  };

  const loadProfile = async () => {
    try {
      const response = await api.get('/users/me/profile');
      setUser(response.data);
      setFullName(response.data.fullName || '');
      setPhone(response.data.phone || '');
      setAvatarUrl(response.data.avatarUrl || '');
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadMfaStatus = async () => {
    try {
      setMfaLoading(true);
      const response = await api.get<MfaStatus>('/auth/mfa/status');
      setMfaStatus(response.data);
    } catch {
      setMfaStatus(null);
    } finally {
      setMfaLoading(false);
    }
  };

  const startMfaEnrollment = async () => {
    try {
      setMfaBusy(true);
      setMfaMessage('');
      const response = await api.post<MfaEnrollment>('/auth/mfa/enroll', {
        friendlyName: 'myKOL authenticator',
      });
      setMfaEnrollment(response.data);
      setMfaCode('');
    } catch (error: any) {
      setMfaMessage(error.response?.data?.error || 'Could not start MFA setup');
    } finally {
      setMfaBusy(false);
    }
  };

  const verifyMfaEnrollment = async () => {
    if (!mfaEnrollment?.factorId || !mfaCode.trim()) {
      setMfaMessage('Enter the 6-digit code from your authenticator app.');
      return;
    }
    try {
      setMfaBusy(true);
      setMfaMessage('');
      const response = await api.post<MfaStatus>('/auth/mfa/enroll/verify', {
        factorId: mfaEnrollment.factorId,
        code: mfaCode.trim(),
      });
      setMfaStatus(response.data);
      setMfaEnrollment(null);
      setMfaCode('');
      setMfaMessage('MFA is enabled for this account.');
    } catch (error: any) {
      setMfaMessage(error.response?.data?.error || 'Could not verify MFA code');
    } finally {
      setMfaBusy(false);
    }
  };

  const qrValue = mfaEnrollment?.uri || mfaEnrollment?.secret || mfaEnrollment?.qrCode || '';

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      await api.put(`/users/${user.id}`, {
        fullName,
        phone,
        avatarUrl,
      });
      await loadProfile();
      await fetchUser({ silent: true });
      alert('Profile updated successfully!');
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      alert(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      alert('New passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    try {
      setChangingPassword(true);
      await api.post('/users/me/change-password', {
        currentPassword,
        newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      alert('Password changed successfully!');
    } catch (error: any) {
      console.error('Failed to change password:', error);
      alert(error.response?.data?.error || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAvatarUpload = (urls: string[]) => {
    if (urls.length > 0) {
      setAvatarUrl(urls[0]);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'AGENCY':
        return 'blue';
      case 'BRAND':
        return 'purple';
      case 'KOL':
        return 'green';
      case 'ADMIN':
        return 'red';
      default:
        return 'gray';
    }
  };

  const getBackPath = () => {
    switch (currentUser?.role) {
      case 'AGENCY':
        return '/agency/dashboard';
      case 'BRAND':
        return '/brand/dashboard';
      case 'KOL':
        return '/kol/dashboard';
      case 'ADMIN':
        return '/admin/dashboard';
      default:
        return '/';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-glass-text-3">Loading...</div>
    );
  }

  if (!user) {
    return <div className="text-center py-12 text-glass-text-3">User not found</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto text-glass-text">
      {/* Omit protected-shell: Agency outlet has no outer shell; that class painted a solid dark slab over the mesh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(getBackPath())}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">My Profile</h1>
            <p className="text-sm text-glass-text-2 mt-1">Manage your account settings</p>
          </div>
        </div>
        <Badge variant={getRoleColor(user.role) as any}>{user.role}</Badge>
      </div>

      {/* Profile Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Profile Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Avatar */}
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {avatarUrl ? (
                <img
                  src={resolveMediaUrl(avatarUrl)}
                  alt={fullName}
                  className="w-24 h-24 rounded-full object-cover border-4 border-glass"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold">
                  {fullName.charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div className="flex-1 space-y-3">
              <h3 className="text-sm font-medium text-glass-text-2">Profile Picture</h3>
              <FileUploader
                onUpload={handleAvatarUpload}
                accept="image/*"
                maxSize={5}
              />
              {avatarUrl && (
                <p className="text-xs text-glass-text-3">
                  Current: {avatarUrl}
                </p>
              )}
            </div>
          </div>

          {/* Form Fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-glass-text-2 mb-2">
                Full Name
              </label>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-glass-text-2 mb-2">
                Email
              </label>
              <div className="flex items-center gap-2 px-3 py-2 bg-glass-2 border border-glass rounded-lg text-glass-text-2">
                <Mail className="w-4 h-4 shrink-0" />
                <span className="text-sm">{user.email}</span>
                <span className="ml-auto text-xs text-glass-text-3">(cannot be changed)</span>
              </div>
            </div>

            {(user.authProviders?.length ?? 0) > 0 && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-glass-text-2 mb-2 flex items-center gap-2">
                  <KeyRound className="w-4 h-4" aria-hidden />
                  Sign-in methods
                </label>
                <div className="flex flex-wrap gap-2">
                  {user.authProviders.map((provider: string) => (
                    <Badge key={provider} variant="outline" className="font-normal">
                      {authProviderLabel(provider)}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-glass-text-3 mt-2">
                  These are the ways you can sign in to this account (e.g. email and Google when both are linked).
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-glass-text-2 mb-2">
                Phone
              </label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+60 12-345 6789"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-glass-text-2 mb-2">
                Role
              </label>
              <div className="px-3 py-2 bg-glass-2 border border-glass rounded-lg">
                <Badge variant={getRoleColor(user.role) as any}>{user.role}</Badge>
              </div>
            </div>
          </div>

          {/* Organizations */}
          {user.memberships && user.memberships.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-glass-text-2 mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4" />
                Organizations
              </label>
              <div className="space-y-2">
                {user.memberships.map((membership: any) => (
                  <div
                    key={membership.id}
                    className="flex items-center justify-between p-3 bg-glass-2 border border-glass rounded-xl"
                  >
                    <div>
                      <div className="font-medium text-glass-text">{membership.organization.name}</div>
                      <div className="text-sm text-glass-text-3">{membership.organization.type}</div>
                    </div>
                    <Badge variant="secondary">{membership.roleInOrg}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Save Button */}
          <div className="flex justify-end pt-4 border-t border-glass">
            <Button onClick={handleSaveProfile} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-glass-text-2">
            Theme (light/dark) is controlled from the sun/moon button in the app header next to the bell.
          </p>
          <div className="space-y-2">
            <p className="text-sm font-medium text-glass-text-2">Delivery</p>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={notifPrefs.inApp}
                onChange={(e) => setNotifPrefs({ ...notifPrefs, inApp: e.target.checked })}
              />
              In-app notifications
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={notifPrefs.email}
                onChange={(e) => setNotifPrefs({ ...notifPrefs, email: e.target.checked })}
              />
              Email (requires SMTP on server)
            </label>
            <Button type="button" variant="outline" size="sm" onClick={saveNotifPrefs}>
              Save notification preferences
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>PDPA / data preferences</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={consent.dataProcessing}
              onChange={(e) => setConsent({ ...consent, dataProcessing: e.target.checked })}
            />
            Essential data processing (required for platform operation)
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={consent.marketingOptIn}
              onChange={(e) => setConsent({ ...consent, marketingOptIn: e.target.checked })}
            />
            Marketing communications opt-in
          </label>
          <Button type="button" variant="outline" size="sm" onClick={saveConsent}>
            Save consent
          </Button>
        </CardContent>
      </Card>

      {showKanbanAccess && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ExternalLink className="w-5 h-5" />
            Kanban Access
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-glass-text-2">
            Open your agency&apos;s Kanban workspace to collect feedback, manage roadmaps, and share updates with brands and KOLs.
          </p>
          <OpenKanbanButton variant="profile" />
        </CardContent>
      </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5" />
            Account Security
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-glass-text-2">
          <div className="flex flex-col gap-3 rounded-xl border border-glass bg-glass-2 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <ShieldCheck className={mfaStatus?.enabled ? 'mt-0.5 h-5 w-5 text-emerald-500' : 'mt-0.5 h-5 w-5 text-glass-text-3'} />
              <div>
                <p className="font-medium text-glass-text">
                  {mfaLoading
                    ? 'Checking Supabase MFA...'
                    : mfaStatus?.enabled
                      ? 'Supabase MFA active'
                      : 'No verified MFA factor found'}
                </p>
                <p className="mt-1 text-xs text-glass-text-3">
                  {mfaStatus?.enabled
                    ? 'Password sign-in now requires an authenticator challenge before myKOL issues an app session.'
                    : 'Add and verify a factor in Supabase Auth to require MFA for this account.'}
                </p>
              </div>
            </div>
            <Button type="button" variant="outline" size="sm" onClick={loadMfaStatus} disabled={mfaLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${mfaLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {mfaStatus?.enabled && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-glass-text-3">Verified factors</p>
              <div className="flex flex-wrap gap-2">
                {mfaStatus.factors.map((factor) => (
                  <Badge key={factor.id} variant="outline" className="font-normal">
                    {factor.friendlyName || factor.type.toUpperCase()}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {!mfaStatus?.enabled && (
            <div className="space-y-4 rounded-xl border border-glass bg-glass-2 p-4">
              {!mfaEnrollment ? (
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium text-glass-text">Set up authenticator app</p>
                    <p className="mt-1 text-xs text-glass-text-3">
                      Generate a QR code for Google Authenticator, Microsoft Authenticator, 1Password, or any TOTP app.
                    </p>
                  </div>
                  <Button type="button" onClick={startMfaEnrollment} disabled={mfaBusy}>
                    <KeyRound className="w-4 h-4 mr-2" />
                    {mfaBusy ? 'Starting...' : 'Set up MFA'}
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4 md:grid-cols-[220px_1fr]">
                  <div className="rounded-lg bg-white p-4">
                    {qrValue ? <QRCode value={qrValue} size={188} /> : null}
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="font-medium text-glass-text">Scan this QR code</p>
                      <p className="mt-1 text-xs text-glass-text-3">
                        After adding it to your authenticator app, enter the 6-digit code to finish setup.
                      </p>
                    </div>
                    {mfaEnrollment.secret && (
                      <div>
                        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-glass-text-3">
                          Manual setup key
                        </p>
                        <code className="block break-all rounded-lg border border-glass bg-glass px-3 py-2 text-xs text-glass-text">
                          {mfaEnrollment.secret}
                        </code>
                      </div>
                    )}
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <Input
                        value={mfaCode}
                        onChange={(e) => setMfaCode(e.target.value)}
                        placeholder="123456"
                        inputMode="numeric"
                        maxLength={12}
                      />
                      <Button type="button" onClick={verifyMfaEnrollment} disabled={mfaBusy}>
                        {mfaBusy ? 'Verifying...' : 'Verify'}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setMfaEnrollment(null);
                          setMfaCode('');
                          setMfaMessage('');
                        }}
                        disabled={mfaBusy}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {mfaMessage && <p className="text-sm text-glass-text-2">{mfaMessage}</p>}
            </div>
          )}

          <p>
            Enrollment and provider policy stay in Supabase, while myKOL enforces the completed AAL2 challenge during
            password sign-in.
          </p>
        </CardContent>
      </Card>

      {/* Change Password */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-glass-text-2 mb-2">
                Current Password
              </label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-glass-text-2 mb-2">
                  New Password
                </label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                  minLength={6}
                />
                <p className="mt-1 text-xs text-glass-text-3">At least 6 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-glass-text-2 mb-2">
                  Confirm New Password
                </label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                  minLength={6}
                />
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-glass">
              <Button type="submit" disabled={changingPassword}>
                <Lock className="w-4 h-4 mr-2" />
                {changingPassword ? 'Changing...' : 'Change Password'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* KOL Profile Link */}
      {user.role === 'KOL' && user.kolProfile && (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-glass-text">KOL Profile</h3>
                <p className="text-sm text-glass-text-2 mt-1">
                  View and manage your influencer profile
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate('/kol/my-profile')}
              >
                Edit KOL Profile
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <p className="text-xs text-glass-text-3 text-center pt-4 pb-2">
        myKOL Platform v2.1.0
      </p>
    </div>
  );
}

